import React, { useCallback, useMemo, useState } from "react";

import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useDeleteHousehold from "@fartherfinance/frontend/api/Households/hooks/useDeleteHousehold";
import useGetHouseholds from "@fartherfinance/frontend/api/Households/hooks/useGetHouseholds";
import {
  Household,
  HouseholdId,
} from "@fartherfinance/frontend/api/Households/types";

import { householdsColumns, HouseholdsTableColumnValue } from "../constants";
import { HouseholdOrderBy, HouseholdTableProps } from "../types";
import { unknownBalanceValue } from "@src/constants/unknownBalanceValue";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import { useGetGenericPagination } from "@src/multiCustodian/hooks/useGetGenericPagination";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { Column, TableSortOrder } from "@src/sharedComponents/Table/Types";
import { handleApiError } from "@src/utils/handleApiError";
import toUSD from "@src/utils/toUSD";

import HouseholdActionMenu, {
  HouseholdMenuActions,
} from "./HouseholdActionMenu";
import HouseholdNameToolTip from "./HouseholdNameToolTip";
import MemberLinkToolTip from "./MemberLinkToolTip";

import styles from "./HouseholdsTable.module.css";

const getSortField = (column: HouseholdsTableColumnValue): HouseholdOrderBy => {
  switch (column) {
    case "establishedAt":
      return "createdAt";
    case "name":
    case "totalAssets":
    case "advisorFirstName":
      return column as HouseholdOrderBy;
    default:
      return "createdAt";
  }
};

type HouseholdsRowData = Record<
  HouseholdsTableColumnValue,
  { value: React.ReactNode }
>;

const DEFAULT_PAGE_SIZE = 10;

const HouseholdsTable: React.FC<HouseholdTableProps> = ({ search }) => {
  const { auth, advisorId } = useAdvisorPageInit();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortColumn, setSortColumn] =
    useState<HouseholdsTableColumnValue>("establishedAt");
  const [sortOrder, setSortOrder] = useState<TableSortOrder>("desc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [householdToDelete, setHouseholdToDelete] = useState<{
    householdId: HouseholdId;
    name: string;
  } | null>(null);

  const notify = useStatusNotification();

  const { mutateAsync: deleteHousehold } = useDeleteHousehold(auth);

  const { data: householdResponseData, isLoading } = useGetHouseholds(
    {
      page: currentPage,
      pageSize,
      orderBy: `${getSortField(sortColumn)} ${sortOrder}`,
      advisorId: advisorId!,
      search: search,
    },
    auth
  );

  const pagination = useGetGenericPagination({
    data: householdResponseData,
    currentPage,
    pageSize,
  });

  const households = useMemo(
    () => householdResponseData?.data ?? [],
    [householdResponseData]
  );

  const openHouseholdDetails = useCallback(
    (household: Household) => () => {
      navigate({
        ...location,
        pathname: `/Advisor/Households/${household.householdId}/Accounts`,
        search: new URLSearchParams({
          advisorId: household.advisorId,
        }).toString(),
      });
    },
    [navigate, location]
  );

  const handleDeleteHousehold = useCallback(async () => {
    if (!householdToDelete) {
      return;
    }

    try {
      await deleteHousehold({ householdId: householdToDelete.householdId });

      // Track household deletion from table
      trackEvent({
        name: "Advisor Delete Household",
        properties: {
          householdId: householdToDelete.householdId,
          householdName: householdToDelete.name,
          source: "table",
        },
      });

      setShowDeleteModal(false);
      setHouseholdToDelete(null);
      notify("Household deleted successfully", "Success");
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error deleting household:",
        genericErrorMessage: "Error deleting household",
        notify,
      });
    }
  }, [householdToDelete, deleteHousehold, notify]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setHouseholdToDelete(null);
  }, []);

  const handleActionSelected = useCallback(
    (
      action: HouseholdMenuActions,
      household: { householdId: HouseholdId; name: string }
    ) => {
      switch (action) {
        case HouseholdMenuActions.Delete:
          setHouseholdToDelete(household);
          setShowDeleteModal(true);
          break;
        default:
          break;
      }
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleColumnSelect = useCallback(
    (column: HouseholdsTableColumnValue, order: TableSortOrder) => {
      setSortColumn(column);
      setSortOrder(order);
      setCurrentPage(1);
    },
    []
  );

  const columns = useMemo((): Column<HouseholdsTableColumnValue>[] => {
    return householdsColumns.map((column) => {
      const isSortable = [
        "name",
        "establishedAt",
        "totalAssets",
        "advisorFirstName",
      ].includes(column.accessor);
      const isActive = column.accessor === sortColumn;
      return isSortable
        ? {
            label: column.label,
            accessor: column.accessor,
            sortable: true as const,
            active: isActive,
            sortOrder: isActive ? sortOrder : "asc",
          }
        : {
            label: column.label,
            accessor: column.accessor,
            sortable: false as const,
          };
    });
  }, [sortColumn, sortOrder]);

  const createLoadingRow = useMemo(() => {
    const row = householdsColumns.reduce((acc, column) => {
      acc[column.accessor] = { value: <Skeleton /> };
      return acc;
    }, {} as HouseholdsRowData);
    return { key: "loading", ...row };
  }, []);

  const createEmptyRow = useMemo(() => {
    const row = householdsColumns.reduce((acc, column) => {
      acc[column.accessor] = {
        value: column.accessor === "name" ? "No households found" : "",
      };
      return acc;
    }, {} as HouseholdsRowData);
    return { key: "empty", ...row };
  }, []);

  const tableRowData = useMemo(() => {
    if (households && households.length > 0) {
      return households.map((household) => ({
        key: household.householdId,
        name: {
          value: (
            <HouseholdNameToolTip
              householdId={household.householdId}
              householdName={household.name}
            />
          ),
        },
        onClick: openHouseholdDetails(household),
        contacts: {
          value: <MemberLinkToolTip members={household.members} />,
        },
        totalAssets: {
          value: toUSD(
            household.totalAssets ? household.totalAssets.toNumber() : 0
          ),
        },
        advisorFirstName: {
          value: `${household.advisorFirstName ?? "-"} ${
            household.advisorLastName ?? " -"
          }`,
        },
        establishedAt: {
          value: household.establishedAt
            ? format(new Date(household.establishedAt), "M/d/yyyy")
            : unknownBalanceValue,
        },
        actions: {
          value: (
            <HouseholdActionMenu
              household={household}
              onActionSelected={handleActionSelected}
            />
          ),
        },
      }));
    }

    if (isLoading) {
      return Array.from({ length: DEFAULT_PAGE_SIZE }, (_, idx) => ({
        ...createLoadingRow,
        key: `loading-${idx}`,
      }));
    }
    return [createEmptyRow];
  }, [
    households,
    isLoading,
    createLoadingRow,
    createEmptyRow,
    openHouseholdDetails,
    handleActionSelected,
  ]);

  return (
    <>
      <PaginatedTable
        isLoading={isLoading}
        columns={columns}
        rows={tableRowData}
        pagination={pagination}
        rowClassName={styles.tableRow}
        onPageChange={handlePageChange}
        onColumnSelect={handleColumnSelect}
        showEmptyRows={false}
      />
      <DeleteModal
        isOpen={showDeleteModal}
        title="Delete Household"
        text={`Deleting "${
          householdToDelete?.name ?? ""
        }" will remove it and all associated members. This action cannot be undone.`}
        deleteButtonText="Delete Household"
        onDelete={handleDeleteHousehold}
        onClose={handleCloseDeleteModal}
      />
    </>
  );
};

export default HouseholdsTable;
